import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import { IcbtCommunication } from "api/types";
import { useGetAllProgressForUsers } from "api/useIcbtQueries";
import {
  FeedbackModalStack,
  ModalState,
} from "components/patients/patientCard/icbt/modals/FeedbackModal/FeedbackModalStack";
import { Patient } from "core/types/index";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { unique } from "remeda";
import { styled } from "styled-components";
import {
  doneCommunicationCategories,
  useCategorizedDoneCommunication,
  type Categories,
} from "../hooks/useCategorizedDoneCommunication";
import { useIcbtLanguage } from "../hooks/useTreatment";
import {
  FeedbackItem,
  HandlerProps,
  UserAnswersByRequestIdByUserId,
} from "./FeedbackItem";
import { useFlag } from "@mindlercare/unleash-client";
import { useGetAllProgramsAndTreatmentsQuery } from "../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { QuestionnaireCounts } from "hooks/icbt/useGetQuestionnaireCountsPerProgramQuery";
import { useQueryState, parseAsStringLiteral } from "nuqs";

const Categories = styled.div`
  display: flex;
  margin-bottom: 20px;
  gap: 8px;
`;

const Category = styled.div<{ $active: boolean }>`
  padding: 10px 20px;
  cursor: pointer;
  color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_WHITE
      : defaultTheme.COLORS.GRAY_BLACK};
  border: ${(props) =>
    props.$active
      ? `2px solid ${defaultTheme.COLORS.GRAY_BLACK}`
      : `1px solid ${defaultTheme.COLORS.GRAY_MEDIUM}`};
  background-color: ${(props) =>
    props.$active
      ? defaultTheme.COLORS.GRAY_BLACK
      : defaultTheme.COLORS.GRAY_WHITE};
  border-radius: 100px;
  &:hover {
    color: ${defaultTheme.COLORS.GRAY_WHITE};
    background-color: ${defaultTheme.COLORS.GRAY_BLACK};
  }
`;

const FeedbackList = styled.ul`
  display: flex;
  flex-direction: column;
`;

export const DoneFeedbackTable = ({
  icbtCommunication,
  patients,
  questionnaireCounts,
  userAnswersByRequestIdByUserId,
}: {
  icbtCommunication?: IcbtCommunication[];
  patients?: { firstName: string | null; lastName: string | null }[];
  questionnaireCounts: QuestionnaireCounts;
  userAnswersByRequestIdByUserId: UserAnswersByRequestIdByUserId;
}) => {
  const { t } = useTranslation();

  const [modalPropsState, setModalPropsState] = useState<ModalState>({
    programId: "",
    treatmentId: "",
    userId: undefined,
    icbtRequestId: "",
    icbtFeedbackDate: "",
    icbtFeedbackDateIgnored: false,
    patientName: "",
    assignIcbtModalOpen: false,
    feedbackModalOpen: false,
  });

  const [tab, setTabState] = useQueryState(
    "state",
    parseAsStringLiteral(doneCommunicationCategories).withDefault("all"),
  );

  const handleTabChange = (newTab: Categories) => setTabState(newTab);

  const { data: progressByUser } = useGetAllProgressForUsers(
    unique(icbtCommunication?.map((icbt) => icbt.patient_user_id) ?? []),
  );
  const language = useIcbtLanguage();

  // TODO: loading state and error handling plz
  const { data: programsAndTreatments } = useGetAllProgramsAndTreatmentsQuery(
    language.codeISO2,
  );

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  const categories = {
    all: t("ALL"),
    week: t("THIS_WEEK"),
    month: t("THIS_MONTH"),
    ...(ignoreFeedbackDateFeatureEnabled
      ? { noFeedbackNeeded: t("NO_FEEDBACK_NEEDED_TAB") }
      : {}),
  };

  const doneCategorized = useCategorizedDoneCommunication({
    icbtCommunication: icbtCommunication,
    patientsToIncludeInCommunication: (patients as Patient[]) ?? [],
    ignoreFeedbackDateFeatureEnabled,
  });

  const feedbackData = doneCategorized.get(tab);

  return (
    <>
      <Categories>
        {Object.entries(categories).map(([key, label]) => (
          <Category
            key={key}
            $active={tab === key}
            onClick={() => handleTabChange(key as Categories)}
          >
            {`${label} ${doneCategorized.get(key as Categories)?.length ?? ""}`}
          </Category>
        ))}
      </Categories>
      <FeedbackList>
        {feedbackData?.map((item, i) => {
          return (
            <FeedbackItem
              displayDropdownUpwards={
                feedbackData.length > 1 && feedbackData.length === i + 1
              }
              key={item.request_id}
              done
              item={item}
              progress={
                progressByUser?.[item.patient_user_id]?.[item.program_id]
              }
              programsAndTreatments={programsAndTreatments}
              questionnaireCounts={questionnaireCounts}
              userAnswersByRequestIdByUserId={userAnswersByRequestIdByUserId}
              onClick={({ icbtCommunication }: HandlerProps) => {
                setModalPropsState({
                  icbtRequestId: icbtCommunication.request_id,
                  patientName: `${item.patient?.firstName} ${item.patient?.lastName}`,
                  programId: icbtCommunication.program_id,
                  treatmentId: icbtCommunication.treatment_id,
                  userId: Number(icbtCommunication.patient_user_id),
                  feedbackModalOpen: true,
                  icbtFeedbackDate: icbtCommunication.feedback_date,
                  icbtFeedbackDateIgnored:
                    icbtCommunication.feedback_date_ignored_at !== null,
                });
              }}
            />
          );
        })}
      </FeedbackList>
      <FeedbackModalStack
        modalState={modalPropsState}
        setModalState={setModalPropsState}
      />
    </>
  );
};
